import { useAnimatedNumber } from "@tamagui/animations-react-native";
import React from "react";
import {
  Animated,
  PanResponder,
  Platform,
  useWindowDimensions,
} from "react-native";
import { Portal, getTokenValue, useMedia, useTheme } from "tamagui";

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));
const MIN_TAP_DISTANCE = 6;

const FloatingTile = ({
  children,
  onPress,
}: {
  children: React.ReactNode;
  onPress?: () => void;
}) => {
  const theme = useTheme();
  const { lg } = useMedia();
  const tileWidth = lg ? 240 : 150;
  const tileHeight = lg ? 320 : 200;
  const tileMargin = getTokenValue("$150");
  const { width: viewportWidth, height: viewportHeight } =
    useWindowDimensions();

  const bounds = React.useMemo(
    () => ({
      minX: tileMargin,
      maxX: viewportWidth - tileWidth - tileMargin,
      minY: tileMargin,
      maxY: viewportHeight - tileHeight - tileMargin,
      midX: (viewportWidth - tileMargin) / 2,
    }),
    [viewportWidth, viewportHeight, tileWidth, tileHeight, tileMargin],
  );
  const x = useAnimatedNumber(bounds.maxX);
  const y = useAnimatedNumber(getTokenValue("$500"));

  const startPositionRef = React.useRef({ x: 0, y: 0 });

  const panResponder = React.useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        // Stop any ongoing animations
        x.stop();
        y.stop();
        // Store the initial position of the tile
        startPositionRef.current = {
          x: x.getValue(),
          y: y.getValue(),
        };
      },
      onPanResponderMove: (_, g) => {
        // Clamp movement to stay within the screen bounds
        const newX = clamp(
          startPositionRef.current.x + g.dx,
          bounds.minX,
          bounds.maxX,
        );
        const newY = clamp(
          startPositionRef.current.y + g.dy,
          bounds.minY,
          bounds.maxY,
        );
        x.setValue(newX, { type: "direct" });
        y.setValue(newY, { type: "direct" });
      },

      onPanResponderRelease: (_, gesture) => {
        const isTap =
          Math.abs(gesture.dx) < MIN_TAP_DISTANCE &&
          Math.abs(gesture.dy) < MIN_TAP_DISTANCE;
        if (isTap) {
          onPress?.();
          return;
        }
        // If the pan gesture was a drag, snap the tile to the closest side
        const tileCenterX = x.getValue() + tileWidth / 2;
        const snapX = tileCenterX < bounds.midX ? bounds.minX : bounds.maxX;
        x.setValue(snapX, {
          type: "spring",
          overshootClamping: true,
        });
      },
    }),
  ).current;

  return (
    // @ts-ignore - pointerEvents is valid but not in types
    <Portal zIndex={100000000} pointerEvents="box-none">
      <Animated.View
        {...panResponder.panHandlers}
        accessible
        role="region"
        aria-label="Floating video call"
        pointerEvents="auto"
        style={[
          {
            position: "absolute",
            width: tileWidth,
            height: tileHeight,
            borderRadius: getTokenValue("$150"),
            overflow: "hidden",
            elevation: 10, // Provides shadow on Android
            shadowColor: theme.neutral1000?.get(),
            // iOS shadow properties
            shadowOpacity: 0.2,
            shadowOffset: { width: 0, height: 5 },
            shadowRadius: getTokenValue("$50"),
          },
          // @ts-ignore - cursor and touchAction are valid on web but not in RN types
          Platform.OS === "web" && {
            cursor: "grab",
            touchAction: "none",
            boxShadow: `0 ${getTokenValue("$50")}px ${getTokenValue("$100")}px ${theme.overlay?.get()}`,
          },
          {
            transform: [
              { translateX: x.getInstance() },
              { translateY: y.getInstance() },
            ],
          },
        ]}
      >
        {children}
      </Animated.View>
    </Portal>
  );
};

export default FloatingTile;
